import type { InitiateOtpResponse } from '@/api';
import { DEFAULT_API_ERROR_MESSAGE } from './apiError';
import { MS_PER_SECOND } from './time';

export const OTP_CHALLENGE_STORAGE_KEY = 'creatorOtpChallenge';

export const OTP_MAX_ATTEMPTS = 3;
export const OTP_RESEND_COOLDOWN_MS = 30 * MS_PER_SECOND;

/** Toasts for OTP request outcomes; fired from the request handlers, not from screen state. */
export const OTP_TOAST = {
  CODE_SENT: { variant: 'success', message: 'Code sent' },
  INCORRECT_CODE: { variant: 'error', message: "That code didn't work." },
  TOO_MANY_REQUESTS: { variant: 'error', message: 'Too many requests' },
  REQUEST_FAILED: { variant: 'error', message: DEFAULT_API_ERROR_MESSAGE },
} as const;

export const OTP_STATE = {
  ENTRY: 'entry',
  VERIFYING: 'verifying',
  INCORRECT: 'incorrect',
  ATTEMPTS_EXHAUSTED: 'attempts-exhausted',
  COOLDOWN: 'cooldown',
  SEND_LIMITED: 'send-limited',
  BLOCKED: 'blocked',
  ERROR: 'error',
} as const;

export type OtpState = (typeof OTP_STATE)[keyof typeof OTP_STATE];

interface OtpChallengeMeta {
  /** The email the creator typed; unset for returning creators, whose email the gateway resolves from the invite. */
  email?: string;
  attemptsRemaining?: number;
}

interface StoredOtpChallenge extends OtpChallengeMeta {
  magicToken: string;
  challenge: InitiateOtpResponse;
}

/** Returns false when storage is unavailable (private mode, quota, blocked cookies). */
export function storeOtpChallenge(
  magicToken: string,
  challenge: InitiateOtpResponse,
  { email, attemptsRemaining }: OtpChallengeMeta = {}
): boolean {
  try {
    const stored: StoredOtpChallenge = { magicToken, challenge };
    if (email) stored.email = email;
    if (attemptsRemaining !== undefined) stored.attemptsRemaining = attemptsRemaining;
    sessionStorage.setItem(OTP_CHALLENGE_STORAGE_KEY, JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
}

function readStored(magicToken: string): Partial<StoredOtpChallenge> | null {
  if (!magicToken) return null;
  try {
    const raw = sessionStorage.getItem(OTP_CHALLENGE_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredOtpChallenge> | null;
    return stored?.magicToken === magicToken ? stored : null;
  } catch {
    return null;
  }
}

/** The stored challenge, only if it was issued for this magic token. */
export function readOtpChallenge(magicToken: string): InitiateOtpResponse | null {
  return readStored(magicToken)?.challenge ?? null;
}

/** The email the creator typed for the stored challenge; undefined for returning creators. */
export function readOtpEmail(magicToken: string): string | undefined {
  const email = readStored(magicToken)?.email;
  return typeof email === 'string' && email ? email : undefined;
}

/** Attempts left for the stored challenge; a full set when none was recorded. */
export function readOtpAttemptsRemaining(magicToken: string): number {
  const value = readStored(magicToken)?.attemptsRemaining;
  const isValid = Number.isInteger(value) && value! >= 0 && value! <= OTP_MAX_ATTEMPTS;
  return isValid ? (value as number) : OTP_MAX_ATTEMPTS;
}

export function clearOtpChallenge(): void {
  try {
    sessionStorage.removeItem(OTP_CHALLENGE_STORAGE_KEY);
  } catch {
    // Storage unavailable: there is nothing stored to clear.
  }
}
